import { approximately, clamp01, EPSILON } from './MathUtils';

const HEX_PATTERN = /^#?(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const SHORT_FORM_MAX_LENGTH = 4;
const BYTE_MAX = 255;
const HEX_RADIX = 16;

/**
 * An RGBA color with components in the zero to one range.
 *
 * Kite's own color type, so nothing outside the renderer adapter has to know how the backend spells
 * colors. Components are plain numbers, which keeps components that hold a color serializable.
 *
 * Channels are sRGB, the space the hex strings in content data are already written in: `fromHex`
 * divides each byte by 255 and nothing else. Converting to whatever space the backend works in is the
 * adapter's job.
 */
export class Color {
  /** Red channel, zero to one. */
  r: number;
  /** Green channel, zero to one. */
  g: number;
  /** Blue channel, zero to one. */
  b: number;
  /** Alpha channel, zero to one, where zero is fully transparent. */
  a: number;

  constructor(r = 1, g = 1, b = 1, a = 1) {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;
  }

  /** Opaque white. */
  static white(): Color {
    return new Color(1, 1, 1, 1);
  }

  /** Opaque black. */
  static black(): Color {
    return new Color(0, 0, 0, 1);
  }

  /** Fully transparent black, the usual clear color for a layered scene. */
  static transparent(): Color {
    return new Color(0, 0, 0, 0);
  }

  /**
   * Parses a CSS style hex string in `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa` form.
   *
   * Throws on anything else rather than silently producing magenta, because a typo in content data
   * should fail where it is written and not halfway through a play session.
   */
  static fromHex(hex: string): Color {
    if (!HEX_PATTERN.test(hex)) {
      throw new Error(`"${hex}" is not a valid hex color; expected #rgb, #rgba, #rrggbb or #rrggbbaa.`);
    }

    const digits = hex.startsWith('#') ? hex.slice(1) : hex;
    const isShortForm = digits.length <= SHORT_FORM_MAX_LENGTH;
    const digitsPerChannel = isShortForm ? 1 : 2;
    const channelCount = digits.length / digitsPerChannel;

    const readChannel = (index: number): number => {
      if (index >= channelCount) {
        return 1;
      }
      const start = index * digitsPerChannel;
      const slice = digits.slice(start, start + digitsPerChannel);
      const doubled = isShortForm ? `${slice}${slice}` : slice;
      return Number.parseInt(doubled, HEX_RADIX) / BYTE_MAX;
    };

    return new Color(readChannel(0), readChannel(1), readChannel(2), readChannel(3));
  }

  /** Builds a color from byte components in the zero to 255 range. */
  static fromBytes(r: number, g: number, b: number, a = BYTE_MAX): Color {
    return new Color(r / BYTE_MAX, g / BYTE_MAX, b / BYTE_MAX, a / BYTE_MAX);
  }

  /** Overwrites every channel. */
  set(r: number, g: number, b: number, a = 1): this {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;
    return this;
  }

  /** Copies every channel from `source`. */
  copyFrom(source: Color): this {
    return this.set(source.r, source.g, source.b, source.a);
  }

  /** An independent copy of this color. */
  clone(): Color {
    return new Color(this.r, this.g, this.b, this.a);
  }

  /** Clamps every channel into the zero to one range. */
  clamp(): this {
    return this.set(clamp01(this.r), clamp01(this.g), clamp01(this.b), clamp01(this.a));
  }

  /** Packs the color into `#rrggbbaa`, always in the long form so round-tripping is lossless. */
  toHex(): string {
    const channel = (value: number): string =>
      Math.round(clamp01(value) * BYTE_MAX)
        .toString(HEX_RADIX)
        .padStart(2, '0');
    return `#${channel(this.r)}${channel(this.g)}${channel(this.b)}${channel(this.a)}`;
  }

  /** Reports whether every channel matches `other` to within `tolerance`. */
  equals(other: Color, tolerance: number = EPSILON): boolean {
    return (
      approximately(this.r, other.r, tolerance) &&
      approximately(this.g, other.g, tolerance) &&
      approximately(this.b, other.b, tolerance) &&
      approximately(this.a, other.a, tolerance)
    );
  }
}
